import { SimpleDocCommand } from '../../DocsApp/commands/v5/types';
import { StandardToolResult } from '../../../types/standardToolResults';
import { CommandExecutionContext } from '../../../types/commands';
import { createFileLogger } from '../../../utils/logger';
import fetchService from '../../../services/fetchService';
import { API_BASE_URL } from '../../../api/config';
import DOMPurify from 'dompurify';
import { schemaToParameters } from '../../../utils/schemaToParameters';

const log = createFileLogger('calendar_read', { enabled: false });

const CALENDAR_INPUT_SCHEMA = {
  type: 'object',
  properties: {
    startDate:  { type: 'string', description: 'Start date (ISO 8601 or relative). Required.' },
    endDate:    { type: 'string', description: 'End date (ISO 8601 or relative). Required.' },
    query:      { type: 'string', description: 'Filter events by title or description keyword' },
    maxResults: { type: 'number', description: 'Max events to return (default 50, max 200)' },
  },
  required: ['startDate', 'endDate'],
};

function parseRelativeDate(dateStr: string): string {
  const normalizedInput = dateStr.toLowerCase().trim();

  if (dateStr.match(/^\d{4}-\d{2}-\d{2}/)) {
    return dateStr;
  }

  switch (normalizedInput) {
    case 'today': {
      const date = new Date();
      date.setHours(0, 0, 0, 0);
      return date.toISOString();
    }
    case 'tomorrow': {
      const date = new Date();
      date.setDate(date.getDate() + 1);
      date.setHours(0, 0, 0, 0);
      return date.toISOString();
    }
    case 'yesterday': {
      const date = new Date();
      date.setDate(date.getDate() - 1);
      date.setHours(0, 0, 0, 0);
      return date.toISOString();
    }
    case 'this week': {
      const date = new Date();
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay());
      weekStart.setHours(0, 0, 0, 0);
      return weekStart.toISOString();
    }
    case 'next week': {
      const date = new Date();
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay() + 7);
      weekStart.setHours(0, 0, 0, 0);
      return weekStart.toISOString();
    }
    default:
      try {
        const parsed = new Date(dateStr);
        if (!isNaN(parsed.getTime())) {
          return parsed.toISOString();
        }
      } catch {}
      throw new Error(`Cannot parse date: "${dateStr}". Use ISO format (YYYY-MM-DD) or relative terms (today, tomorrow, this week).`);
  }
}

export const calendar_read: SimpleDocCommand = {
  id: 'calendar_read',
  logicalName: 'calendar_read',
  version: 'v1',

  description: `Read calendar events for scheduling and availability. startDate and endDate are required (max 90-day range, max 200 events).

**DATE FORMATS:** ISO 8601 ("2026-02-03") or relative ("today", "tomorrow", "this week", "next week")

**LIMITS:** startDate is capped to 7 days in the past; endDate is capped to 90 days ahead. Requests outside these bounds are automatically truncated.

**EXAMPLES:**

Today's schedule:
{ "startDate": "today", "endDate": "today" }

Next week's events:
{ "startDate": "next week", "endDate": "next week" }

Search for specific events:
{ "startDate": "2026-02-01", "endDate": "2026-02-28", "query": "standup" }

**RETURNS:** Array of events with title, start, end, location, attendees, allDay, videoLink`,

  schema: {
    parameters: [
      {
        ...schemaToParameters(CALENDAR_INPUT_SCHEMA).find(p => p.name === 'startDate')!,
        required: true,
        validator: (value: string) => {
          if (!value) return 'startDate is required';
          try { parseRelativeDate(value); return null; } catch (e: any) { return e.message; }
        }
      },
      {
        ...schemaToParameters(CALENDAR_INPUT_SCHEMA).find(p => p.name === 'endDate')!,
        required: true,
        validator: (value: string, params: any) => {
          if (!value) return 'endDate is required';
          try {
            const end = new Date(parseRelativeDate(value));
            const start = new Date(parseRelativeDate(params.startDate));
            if (end <= start) return 'endDate must be after startDate';
            return null;
          } catch (e: any) { return e.message; }
        }
      },
      ...schemaToParameters(CALENDAR_INPUT_SCHEMA).filter(p => p.name !== 'startDate' && p.name !== 'endDate'),
    ]
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    let { startDate, endDate, query, maxResults = 50 } = params;

    log.log('[calendar_read] Reading calendar:', { startDate, endDate, query });

    try {
      try {
        startDate = parseRelativeDate(startDate);
        endDate = parseRelativeDate(endDate);
      } catch (error: any) {
        return {
          status: 'error',
          message: 'Invalid date format',
          error: { code: 'invalid_date', category: 'validation' as const, details: error.message },
          format: 'plaintext'
        };
      }

      const now = new Date();
      const minStart = new Date(now); minStart.setDate(now.getDate() - 7); minStart.setHours(0, 0, 0, 0);
      const maxEnd   = new Date(now); maxEnd.setDate(now.getDate() + 90);  maxEnd.setHours(23, 59, 59, 999);

      let clamped = false;
      if (new Date(startDate) < minStart) { startDate = minStart.toISOString(); clamped = true; }
      if (new Date(endDate)   > maxEnd)   { endDate   = maxEnd.toISOString();   clamped = true; }

      const response = await fetchService(`${API_BASE_URL}/calendar/read`, {
        method: 'POST',
        body: JSON.stringify({ startDate, endDate, query, maxResults })
      });

      const data = response;

      if (data.error || data.detail) {
        const errorDetail = data.detail || data.error;
        if (errorDetail.includes('access is disabled')) {
          return { status: 'error', message: 'Calendar access is disabled', error: { code: 'calendar_access_disabled', category: 'permission' as const, details: errorDetail }, format: 'plaintext' };
        }
        if (errorDetail.includes('not found') || errorDetail.includes('not connected')) {
          return { status: 'error', message: 'Calendar not connected', error: { code: 'calendar_not_connected', category: 'permission' as const, details: errorDetail }, format: 'plaintext' };
        }
        throw new Error(errorDetail);
      }

      if (!data.events || !Array.isArray(data.events)) {
        console.error('[calendar_read] Invalid response structure:', data);
        return { status: 'error', message: 'Invalid response from calendar service', error: { code: 'invalid_response', category: 'execution' as const, details: 'Backend returned incomplete data (missing events array)' }, format: 'plaintext' };
      }

      const events = data.events.map((event: any) => {
        let sanitizedDescription = null;
        if (event.description) {
          sanitizedDescription = DOMPurify.sanitize(event.description, {
            ALLOWED_TAGS: ['p', 'br', 'b', 'i', 'u', 'strong', 'em', 'a'],
            ALLOWED_ATTR: ['href', 'target', 'rel'],
            ALLOW_DATA_ATTR: false
          });
        }
        return {
          id: event.id,
          title: event.summary,
          description: sanitizedDescription,
          location: event.location || null,
          start: event.start,
          end: event.end,
          isAllDay: event.isAllDay,
          status: event.status,
          calendar: event.calendarName,
          timezone: event.timezone || null
        };
      });

      let message = `Found ${events.length} event${events.length !== 1 ? 's' : ''}`;
      if (clamped) message += ' (date range auto-clamped to Google\'s limits: 7 days past / 90 days ahead)';
      if (data.privacyFiltered > 0) message += ` (${data.privacyFiltered} filtered by privacy settings)`;
      if (data.failedCalendars && data.failedCalendars.length > 0) message += `. Warning: ${data.failedCalendars.length} calendar${data.failedCalendars.length !== 1 ? 's' : ''} could not be accessed`;

      return {
        status: 'success',
        message,
        data: { events, totalCount: data.totalCount, privacyFiltered: data.privacyFiltered, dateRange: { start: startDate, end: endDate }, metadata: data.metadata, failedCalendars: data.failedCalendars || [] },
        format: 'json'
      };

    } catch (error: any) {
      log.error('[calendar_read] Error:', error);

      let errorMessage = 'Failed to read calendar';
      let errorDetails = error.message;

      if (error.data) {
        if (error.data.detail) {
          errorDetails = error.data.detail;
          errorMessage = errorDetails;
        } else if (typeof error.data === 'string') {
          errorDetails = error.data;
          errorMessage = errorDetails;
        }
      }

      let errorCode = 'calendar_read_error';
      let errorCategory: 'validation' | 'permission' | 'execution' = 'execution';
      if (error.status === 400) { errorCategory = 'validation'; errorCode = 'invalid_parameters'; }
      else if (error.status === 403) { errorCategory = 'permission'; errorCode = 'access_denied'; }
      else if (error.status === 404) { errorCategory = 'permission'; errorCode = 'not_found'; }

      return { status: 'error', message: errorMessage, error: { code: errorCode, category: errorCategory, details: errorDetails }, format: 'plaintext' };
    }
  }
};
